import React, { useEffect, useState } from "react";
import axios from "axios";
import { Copy, Check, Trash2, Link2, Loader2, Lock } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { Toaster } from "../components/ui/toaster";
import { ValuationProvider } from "../components/ValuationDialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import { DEFAULT_FEES, rateFor } from "../lib/termsFees";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEL = "h-10 w-full rounded-md border border-[#e5ddd0] bg-white px-3 text-sm text-[#16323f] focus:border-[#35a4a4] focus:outline-none";
const SERVICES = ["Full Management", "Rent Collection", "Let Only"];

const linkUrl = (code) => `${window.location.origin}/landlords/terms/${code}`;

function CopyButton({ code }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(linkUrl(code));
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <button type="button" onClick={copy} data-testid={`copy-link-${code}`}
      className="inline-flex items-center gap-1.5 rounded-full bg-[#16323f] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-white transition-colors hover:bg-[#ec008c]">
      {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy link"}
    </button>
  );
}

function LinkRow({ l, onDelete }) {
  const fee = l.service ? rateFor(l, l.service) : `FM ${l.fm_rate}% · RC ${l.rc_rate}% · LO ${l.lo_rate}%`;
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-[#eee6da] bg-[#faf7f0] p-4 sm:flex-row sm:items-center" data-testid={`terms-link-row-${l.code}`}>
      <div className="min-w-0 flex-1">
        <p className="font-display text-sm font-600 text-[#16323f]">{l.landlord_name || "Unnamed landlord"}</p>
        <p className="text-xs text-[#7a716a]">{l.service || "Landlord chooses"} &middot; {fee} &middot; £{l.arrangement_fee} arrangement
          {l.used_count ? <span className="ml-2 font-semibold text-[#35a4a4]">Signed {l.used_count}×</span> : null}</p>
        <a href={linkUrl(l.code)} target="_blank" rel="noopener noreferrer" className="mt-1 block truncate font-mono text-xs text-[#ec008c]">{linkUrl(l.code)}</a>
      </div>
      <div className="flex items-center gap-2">
        <CopyButton code={l.code} />
        <button type="button" onClick={() => onDelete(l.code)} data-testid={`delete-link-${l.code}`}
          className="grid h-9 w-9 place-items-center rounded-full border border-[#e5ddd0] text-[#7a716a] transition-colors hover:border-red-400 hover:text-red-500">
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );
}

function Content() {
  const { toast } = useToast();
  const [form, setForm] = useState({ landlord_name: "", service: "Full Management", note: "", ...DEFAULT_FEES });
  const [links, setLinks] = useState([]);
  const [created, setCreated] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => axios.get(`${API}/terms-links`).then((r) => setLinks(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await axios.post(`${API}/terms-links`, form);
      setCreated(r.data);
      await navigator.clipboard.writeText(linkUrl(r.data.code)).catch(() => {});
      toast({ title: "Private link created", description: "The link has been copied to your clipboard." });
      load();
    } catch {
      toast({ title: "Couldn't create link", description: "Check the fee values and try again.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (code) => {
    await axios.delete(`${API}/terms-links/${code}`);
    if (created?.code === code) setCreated(null);
    load();
  };

  return (
    <div className="App">
      <Seo title="Private Terms Links | New Space" description="Internal tool" path="/landlords/terms/admin" noindex />
      <Header />
      <section className="relative overflow-hidden bg-[#16323f] pt-32 pb-14 lg:pt-40">
        <div className="mx-auto max-w-[900px] px-5 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#d8b074]">Internal &middot; not linked from the site</p>
          <h1 className="mt-3 font-display text-4xl font-700 uppercase tracking-wide text-white lg:text-5xl">Private Terms Links</h1>
          <p className="mt-3 text-white/80">Generate a one-off Terms of Business link with a reduced fee for a specific landlord.</p>
        </div>
      </section>

      <section className="bg-[#faf7f0] py-14 lg:py-20">
        <div className="mx-auto max-w-[900px] px-5">
          <form onSubmit={submit} className="rounded-2xl border border-[#e5ddd0] bg-white p-7 shadow-sm lg:p-9" data-testid="terms-link-form">
            <h2 className="font-display text-xl font-600 uppercase tracking-wide text-[#16323f]">New private link</h2>
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="ll-name">Landlord name *</Label>
                <Input id="ll-name" data-testid="link-landlord-name" required value={form.landlord_name} onChange={(e) => set("landlord_name", e.target.value)} placeholder="Jane Smith" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ll-service">Lock to service</Label>
                <select id="ll-service" data-testid="link-service" className={SEL} value={form.service} onChange={(e) => set("service", e.target.value)}>
                  {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                  <option value="">Let the landlord choose</option>
                </select>
              </div>
            </div>
            <div className="mt-5 grid gap-5 sm:grid-cols-4">
              {[["fm_rate", "Full Management %"], ["rc_rate", "Rent Collection %"], ["lo_rate", "Let Only %"], ["arrangement_fee", "Arrangement fee £"]].map(([k, label]) => (
                <div key={k} className="space-y-1.5">
                  <Label htmlFor={`ll-${k}`}>{label}</Label>
                  <Input id={`ll-${k}`} data-testid={`link-${k.replace(/_/g, "-")}`} type="number" step="0.5" min="0" value={form[k]} onChange={(e) => set(k, e.target.value)} />
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-[#7a716a]">Standard rates: 12% / 8% / 7% + VAT and a £180 arrangement fee. Enter 0 for no arrangement fee.</p>
            <div className="mt-5 space-y-1.5">
              <Label htmlFor="ll-note">Internal note (optional)</Label>
              <Input id="ll-note" data-testid="link-note" value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="e.g. 3-property portfolio, agreed by Kim" />
            </div>
            <button type="submit" disabled={saving} data-testid="create-link-button"
              className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-[#ec008c] py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#c60074] disabled:opacity-70">
              {saving ? <><Loader2 size={16} className="animate-spin" /> Creating...</> : <><Link2 size={16} /> Create private link</>}
            </button>
          </form>

          {created && (
            <div className="mt-6 rounded-2xl border-2 border-[#35a4a4] bg-white p-6 shadow-sm" data-testid="created-link-card">
              <div className="flex items-center gap-2 text-[#35a4a4]"><Lock size={16} /><p className="text-xs font-semibold uppercase tracking-[0.2em]">Link ready to send</p></div>
              <p className="mt-2 font-display text-lg font-600 text-[#16323f]">{created.landlord_name} &middot; {created.service || "Landlord chooses"}{created.service && ` at ${rateFor(created, created.service)}`}</p>
              <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
                <code data-testid="created-link-url" className="flex-1 truncate rounded-md bg-[#faf7f0] px-3 py-2 font-mono text-sm text-[#ec008c]">{linkUrl(created.code)}</code>
                <CopyButton code={created.code} />
              </div>
            </div>
          )}

          <div className="mt-10">
            <h2 className="font-display text-xl font-600 uppercase tracking-wide text-[#16323f]">Existing links</h2>
            <div className="mt-4 space-y-3" data-testid="terms-links-list">
              {links.length === 0 && <p className="text-sm text-[#7a716a]">No private links yet.</p>}
              {links.map((l) => <LinkRow key={l.code} l={l} onDelete={remove} />)}
            </div>
          </div>
        </div>
      </section>
      <Footer />
      <Toaster />
    </div>
  );
}

export default function TermsLinksAdmin() {
  return (
    <ValuationProvider>
      <Content />
    </ValuationProvider>
  );
}
